import React, { useEffect } from 'react';
//import { logPageView } from '../utils/analytics';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Building, 
  Phone, 
  Mail, 
  MapPin, 
  FileText,
  Scale,
  Sparkles,
  User
} from 'lucide-react';

const Impressum = () => {
  useEffect(() => {
    // Google Analytics
    //logPageView(); // Or create a logEvent function for custom events

    // SEO Meta Tags
    document.title = 'Impressum - Schteffann IT-Dienstleistungen Mönchengladbach | Stephan Schunter';
    
    // Meta Description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Impressum der Schteffann IT-Dienstleistungen. Angaben gemäß §5 TMG für IT-Services, Digitalisierung und Cloud-Lösungen in Mönchengladbach. Stephan Schunter.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Impressum der Schteffann IT-Dienstleistungen. Angaben gemäß §5 TMG für IT-Services, Digitalisierung und Cloud-Lösungen in Mönchengladbach. Stephan Schunter.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      const keywords = document.createElement('meta');
      keywords.name = 'keywords';
      keywords.content = 'Impressum, Schteffann, IT-Dienstleistungen Mönchengladbach, Stephan Schunter, IT-Services, Kontaktdaten';
      document.getElementsByTagName('head')[0].appendChild(keywords);
    }

    return () => {
      document.title = 'Schteffann - IT-Dienstleistungen für KMU';
    };
  }, []);

  const legalSections = [
    {
      icon: Building,
      title: "Anbieter",
      content: [
        { label: "Unternehmen", value: "Schteffann" },
        { label: "Inhaber", value: "Stephan Schunter" },
        { label: "Straße", value: "Gladbacher Str. 50" },
        { label: "PLZ/Ort", value: "41179 Mönchengladbach" },
        { label: "Land", value: "Deutschland" }
      ],
      gradient: "from-cyan-100 to-blue-100"
    },
    {
      icon: Phone,
      title: "Kontakt",
      content: [
        { label: "Telefon", value: "0156-79697252", link: "tel:015679697252" },
        { label: "E-Mail", value: "kontakt(@)schteffann.de" }
      ],
      gradient: "from-blue-100 to-cyan-100"
    },
    {
      icon: Scale,
      title: "Umsatzsteuer-ID",
      content: [
        { label: "USt-ID", value: "DE455112705" }
      ],
      gradient: "from-cyan-100 to-blue-100"
    },
    {
      icon: User,
      title: "Verantwortlich für den Inhalt",
      content: [
        { label: "Name", value: "Stephan Schunter" },
        { label: "Anschrift", value: "Gladbacher Str. 50, 41179 Mönchengladbach" },
        { label: "Rechtsgrundlage", value: "§ 55 Abs. 2 RStV" }
      ],
      gradient: "from-blue-100 to-cyan-100"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400/15 to-blue-500/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-r from-blue-400/10 to-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="container relative">
          <div className="text-center mb-4">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 hover:from-cyan-200 hover:to-blue-200 px-6 py-2 text-sm font-medium">
              <FileText className="w-4 h-4 mr-2" />
              Rechtliches
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Impressum</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Angaben gemäß § 5 TMG für die Schteffann IT-Dienstleistungen
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container max-w-6xl mx-auto">
          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {legalSections.map((section, index) => {
              const IconComponent = section.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-500 border-0 bg-white hover:bg-gradient-to-br hover:from-white hover:to-cyan-50/30 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <CardHeader className="relative">
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br ${section.gradient} rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <IconComponent className="h-8 w-8 text-cyan-600" />
                      </div>
                      <CardTitle className="text-2xl text-slate-900 group-hover:text-cyan-700 transition-colors">
                        {section.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4 relative">
                    {section.content.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex justify-between items-start">
                        <span className="font-medium text-slate-700 min-w-24">{item.label}:</span>
                        {item.link ? (
                          <a 
                            href={item.link}
                            className="text-slate-900 hover:text-cyan-600 transition-colors font-medium text-right flex-1"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <span className="text-slate-900 font-medium text-right flex-1">
                            {item.value}
                            {item.note && (
                              <span className="block text-xs text-slate-500 mt-1">{item.note}</span>
                            )}
                          </span>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Legal Content */}
          <div className="space-y-8">
            {/* Haftung für Inhalte */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <div className="w-2 h-8 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full mr-4"></div>
                  Haftung für Inhalte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600 leading-relaxed relative">
                <p>
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten 
                  nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als 
                  Diensteanbieter jedoch nicht unter der Verpflichtung, übermittelte oder gespeicherte 
                  fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine 
                  rechtswidrige Tätigkeit hinweisen.
                </p>
                <p>
                  Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den 
                  allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch 
                  erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei 
                  Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
                </p>
              </CardContent>
            </Card>

            {/* Haftung für Links */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-cyan-600 rounded-full mr-4"></div>
                  Haftung für Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600 leading-relaxed relative">
                <p>
                  Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen 
                  Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der 
                  Seiten verantwortlich.
                </p>
                <p>
                  Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße 
                  überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine 
                  permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte 
                  einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir 
                  derartige Links umgehend entfernen.
                </p>
              </CardContent>
            </Card>

            {/* Urheberrecht */}
            <Card className="border-0 bg-white shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <div className="w-2 h-8 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full mr-4"></div>
                  Urheberrecht
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-600 leading-relaxed relative">
                <p>
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen 
                  dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art 
                  der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen 
                  Zustimmung des jeweiligen Autors bzw. Erstellers.
                </p>
                <p>
                  Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch 
                  gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden 
                  die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche 
                  gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, 
                  bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden 
                  wir derartige Inhalte umgehend entfernen.
                </p>
              </CardContent>
            </Card>

            {/* Online-Streitbeilegung */}
            <Card className="border-0 bg-gradient-to-br from-cyan-50 to-blue-50 shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5"></div>
              <CardHeader className="relative">
                <CardTitle className="text-2xl text-slate-900 flex items-center">
                  <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-cyan-600 rounded-full mr-4"></div>
                  Online-Streitbeilegung
                </CardTitle>
              </CardHeader>
              <CardContent className="text-slate-600 leading-relaxed relative">
                <p className="mb-4">
                  Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                  <a 
                    href="https://ec.europa.eu/consumers/odr/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-cyan-600 hover:text-blue-600 transition-colors ml-1 font-medium"
                  >
                    https://ec.europa.eu/consumers/odr/
                  </a>
                </p>
                <p>
                  Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
                  Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-16 text-center">
            <Badge className="bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-3">
              <Sparkles className="w-4 h-4 mr-2" />
              Stand: Juni 2025
            </Badge>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impressum;